/**
 * Extracts a human-readable message from an unknown thrown value.
 *
 * The rule this enforces: a caller must never see an empty error surface. An
 * `Error` carrying an empty message, a plain object whose `message` is an empty
 * string, and a thrown `null` all fall back to the supplied text — `??` alone is
 * not enough here, because it substitutes only on null/undefined and would let
 * an empty string through.
 */
export function errorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'string' && error) return error;
  if (error instanceof Error && error.message) return error.message;
  const m = (error as { message?: unknown } | null | undefined)?.message;
  if (typeof m === 'string' && m) return m;
  return fallback;
}

/**
 * `errorMessage`, plus whatever field-level detail the error carries. Both
 * planes' fetch layers map the backend's 422 `details.fieldErrors` onto the
 * thrown error's `errors` (a `Record<string, string[]>` of Zod issue
 * messages), but the top-level message for those is a bare 'Validation
 * failed' — it names no field and no rule, so a toast built from
 * `errorMessage` alone leaves the operator retrying the same value. The
 * issue messages themselves ("apiVersion must look like v60.0") are the
 * actionable part; append them. An error without that map falls through to
 * `errorMessage` unchanged.
 */
export function errorMessageWithFieldErrors(error: unknown, fallback: string): string {
  const base = errorMessage(error, fallback);
  const fields = (error as { errors?: unknown } | null | undefined)?.errors;
  if (!fields || typeof fields !== 'object') return base;
  const details = Object.values(fields)
    .flatMap((v) => (Array.isArray(v) ? v : []))
    .filter((v): v is string => typeof v === 'string' && v !== '');
  return details.length ? `${base}: ${details.join('; ')}` : base;
}
